import { Checkbox, FormControlLabel } from '@mui/material';
import { useFormContext, Controller } from 'react-hook-form';
import { convertToDisplayName } from '../helpers';
import { GenericFormComponentProps } from '../types';
import formInputStyles from './FormCheckbox.module.scss';
import { useEffect } from 'react';
import { useFormSectionContext } from '../FormSection/FormSection';
import { useFormState } from 'hooks/useFormState';

interface FormCheckboxProps extends GenericFormComponentProps {
    onChange?: () => void;
}
/**
 * Form checkbox input
 * @param {string} field - field to track with react-hook-form
 * @param {string} label - optional label override
 * @param {boolean} disabled - is this component readonly?
 * @param {string} className - for styling
 * @param {string} onChange - custom onChange handler to trigger apart from form logic
 * @returns FormText component
 */
export function FormCheckbox({
    field,
    label,
    disabled,
    className,
    onChange,
}: FormCheckboxProps) {
    const { control, getFieldState } = useFormContext();
    const { setFormSectionError } = useFormState();
    const { formSection } = useFormSectionContext();

    const watchedField = getFieldState(field);

    useEffect(() => {
        setFormSectionError({
            section: formSection,
            field: { name: field, error: !!watchedField.error },
        });
    }, [field, formSection, setFormSectionError, watchedField.error]);

    return (
        <Controller
            name={field}
            control={control}
            render={({ field: fieldProps, fieldState: { error } }) => {
                label = label || convertToDisplayName(field);
                const id = `text-field-${field}`;

                return (
                    <FormControlLabel
                        id={id}
                        label={label}
                        control={<Checkbox checked={fieldProps.value} />}
                        color={error ? 'error' : 'primary'}
                        className={`${formInputStyles.formCheckbox} ${className}`}
                        disabled={disabled}
                        {...fieldProps}
                        onChange={(event: any) => {
                            fieldProps.onChange(event);
                            if (onChange) {
                                onChange();
                            }
                        }}
                    />
                );
            }}
        />
    );
}
